import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { readJSON, writeJSON, StorageKey } from '@/lib/jumble';

interface SocialState {
  bookmarks: string[];
  toggleBookmark: (id: string) => void;
  isBookmarked: (id: string) => boolean;
  mutedPubkeys: string[];
  toggleMutePubkey: (pubkey: string) => void;
  isMuted: (pubkey: string) => boolean;
  mutedWords: string[];
  addMutedWord: (word: string) => void;
  removeMutedWord: (word: string) => void;
  pinnedPubkeys: string[];
  togglePin: (pubkey: string) => void;
  showReplies: boolean;
  setShowReplies: (v: boolean) => void;
}

const Ctx = createContext<SocialState | undefined>(undefined);

export function useSocial() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSocial must be used within SocialProvider');
  return ctx;
}

export function SocialProvider({ children }: { children: React.ReactNode }) {
  const [bookmarks, setBookmarks] = useState<string[]>(() => readJSON<string[]>(StorageKey.BOOKMARKS, []));
  const [mutedPubkeys, setMuted] = useState<string[]>(() => readJSON<string[]>(StorageKey.MUTED_PUBKEYS, []));
  const [mutedWords, setMutedWords] = useState<string[]>(() => readJSON<string[]>(StorageKey.MUTED_WORDS, []));
  const [pinnedPubkeys, setPinned] = useState<string[]>(() => readJSON<string[]>(StorageKey.PINNED_PUBKEYS, []));
  const [showReplies, setShowReplies] = useState<boolean>(() => readJSON<boolean>(StorageKey.SHOW_REPLIES, true));

  useEffect(() => writeJSON(StorageKey.BOOKMARKS, bookmarks), [bookmarks]);
  useEffect(() => writeJSON(StorageKey.MUTED_PUBKEYS, mutedPubkeys), [mutedPubkeys]);
  useEffect(() => writeJSON(StorageKey.MUTED_WORDS, mutedWords), [mutedWords]);
  useEffect(() => writeJSON(StorageKey.PINNED_PUBKEYS, pinnedPubkeys), [pinnedPubkeys]);
  useEffect(() => writeJSON(StorageKey.SHOW_REPLIES, showReplies), [showReplies]);

  const toggleBookmark = useCallback((id: string) => {
    setBookmarks((p) => (p.includes(id) ? p.filter((x) => x !== id) : [id, ...p]));
  }, []);
  const toggleMutePubkey = useCallback((pk: string) => {
    setMuted((p) => (p.includes(pk) ? p.filter((x) => x !== pk) : [...p, pk]));
  }, []);
  const togglePin = useCallback((pk: string) => {
    setPinned((p) => (p.includes(pk) ? p.filter((x) => x !== pk) : [...p, pk]));
  }, []);
  const addMutedWord = useCallback((w: string) => {
    const word = w.trim().toLowerCase();
    if (!word) return;
    setMutedWords((p) => (p.includes(word) ? p : [...p, word]));
  }, []);
  const removeMutedWord = useCallback((w: string) => {
    setMutedWords((p) => p.filter((x) => x !== w));
  }, []);

  const value = useMemo<SocialState>(
    () => ({
      bookmarks,
      toggleBookmark,
      isBookmarked: (id) => bookmarks.includes(id),
      mutedPubkeys,
      toggleMutePubkey,
      isMuted: (pk) => mutedPubkeys.includes(pk),
      mutedWords,
      addMutedWord,
      removeMutedWord,
      pinnedPubkeys,
      togglePin,
      showReplies,
      setShowReplies,
    }),
    [bookmarks, toggleBookmark, mutedPubkeys, toggleMutePubkey, mutedWords, addMutedWord, removeMutedWord, pinnedPubkeys, togglePin, showReplies],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Filter helper: hide muted authors / muted words. */
export function isEventMuted(
  content: string,
  pubkey: string,
  mutedPubkeys: string[],
  mutedWords: string[],
): boolean {
  if (mutedPubkeys.includes(pubkey)) return true;
  const lower = content.toLowerCase();
  return mutedWords.some((w) => w && lower.includes(w));
}
